import { useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GitMerge, UserMinus, UserPlus } from 'lucide-react'
import { Avatar, Button, Card, CardBody, CardHeader, CardTitle, seedToColor } from '@ludus/ui'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import type { ManagerPatch } from '@/lib/api/managers.ts'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import { ContactEditor } from '../components/ContactEditor.tsx'
import { ExtensionsEditor } from '../components/ExtensionsEditor.tsx'
import { IdentityBadges } from '../components/IdentityBadges.tsx'
import { LoadFailure } from '../components/LoadFailure.tsx'
import { MergeManagerDialog } from '../components/MergeManagerDialog.tsx'
import { NameEditor } from '../components/NameEditor.tsx'
import { RetireManagerDialog } from '../components/RetireManagerDialog.tsx'
import { saveErrorKey } from '../components/saveError.ts'
import { useManagersScreen } from '../hooks/useManagersScreen.ts'
import { useMergeManagers } from '../hooks/useMergeManagers.ts'
import { useUpdateManager } from '../hooks/useUpdateManager.ts'
import { fullName, initialsOf } from '../utils/managerName.ts'

export function ManagerPage() {
  const { t } = useTranslation()
  const { managerId } = useParams<{ managerId: string }>()
  const { managers, pipelines, isLoading, failure } = useManagersScreen()
  const { save, isMutating } = useUpdateManager()
  const { merge } = useMergeManagers()
  const [saveError, setSaveError] = useState<PipelineApiError | null>(null)
  const [retiring, setRetiring] = useState(false)
  const [merging, setMerging] = useState(false)

  if (isLoading) return <CenteredPanel title={t('pages.managers.loading')} muted />
  if (failure) return <LoadFailure failure={failure} />
  const manager = managers.find((m) => m.id === managerId)
  if (manager === undefined)
    return <CenteredPanel title={t('pages.managers.notFound')} tone="error" />

  const persist = async (patch: ManagerPatch): Promise<boolean> => {
    setSaveError(null)
    try {
      await save(manager.id, patch)
      return true
    } catch (cause) {
      setSaveError(cause as PipelineApiError)
      return false
    }
  }
  const accent = seedToColor(manager.id)

  return (
    <div className="flex w-full max-w-3xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <Breadcrumb
        items={[
          { label: t('pages.managers.title'), to: '/managers' },
          { label: fullName(manager) },
        ]}
      />
      <header className="bg-gradient-panel-elevated relative overflow-hidden rounded-xl border border-border px-4 py-3">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background: `radial-gradient(120% 90% at 0% 0%, color-mix(in srgb, ${accent} 16%, transparent) 0%, transparent 60%)`,
          }}
        />
        <div className="relative flex flex-wrap items-center gap-4">
          <Avatar size="lg" initials={initialsOf(manager)} seed={manager.id} aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <NameEditor manager={manager} disabled={isMutating} onSave={persist} />
            <IdentityBadges manager={manager} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {managers.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                className="text-muted hover:text-foreground"
                disabled={isMutating}
                onClick={() => setMerging(true)}
              >
                <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />
                {t('pages.managers.merge.button')}
              </Button>
            )}
            {manager.isActive ? (
              <Button
                variant="outline"
                size="sm"
                className="text-muted hover:text-foreground"
                disabled={isMutating}
                onClick={() => setRetiring(true)}
              >
                <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
                {t('pages.managers.retire.button')}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={isMutating}
                onClick={() => void persist({ isActive: true })}
              >
                <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                {t('pages.managers.restore')}
              </Button>
            )}
          </div>
        </div>
      </header>
      <Section title={t('pages.managers.contact.title')}>
        <ContactEditor manager={manager} disabled={isMutating} onSave={persist} />
      </Section>
      <Section title={t('pages.managers.extensions.title')}>
        <ExtensionsEditor
          extensions={manager.phoneExtensions ?? []}
          disabled={isMutating}
          onChange={(phoneExtensions) => persist({ phoneExtensions })}
        />
      </Section>
      {saveError && (
        <p role="alert" className="text-[11px] font-medium text-toast-error-text">
          {t(saveErrorKey(saveError))}
        </p>
      )}
      {merging && (
        <MergeManagerDialog
          manager={manager}
          others={managers.filter((m) => m.id !== manager.id)}
          pipelines={pipelines}
          onOpenChange={setMerging}
          onMerge={(absorbedId) => merge({ keepId: manager.id, absorbedId })}
        />
      )}
      {retiring && (
        <RetireManagerDialog
          open
          onOpenChange={setRetiring}
          manager={manager}
          onRetire={() => save(manager.id, { isActive: false })}
        />
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="rounded-xl border-border shadow-none">
      <CardHeader className="border-border/20 py-2.5">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
          {title}
        </CardTitle>
      </CardHeader>
      <CardBody className="py-3">{children}</CardBody>
    </Card>
  )
}
